import React from 'react'
import injectSheet from 'react-jss'
import classNames from 'classnames'
import compose from 'recompose/compose'
import pure from 'recompose/pure'
import withStateHandlers from 'recompose/withStateHandlers'
import withHandlers from 'recompose/withHandlers'
import MoreDescription from './MoreDescription'
import MediaFullScreen from './MediaFullScreen'
import MediaImage from './MediaImage'
import CloseButton from 'aria-viewer/tour/components/buttons/closeButton'
import ShareButton from 'aria-viewer/tour/components/buttons/shareButton'
import AudioButton from 'aria-viewer/tour/components/buttons/audioButton'
import Button from 'aria-viewer/tour/components/buttons/button'
import Clamp from 'aria-viewer/utils/clamp'
import { Modal } from 'aria-viewer/modal'
import { MEDIA_TYPES, NETWORK_TYPES } from 'aria-viewer/tour/constants'
import { StopDescriptionModals } from './constants'
import VrButton, { VR_BUTTON_LOCATION } from 'aria-viewer/tour/components/buttons/vrButton'
import styleguide from 'aria-viewer/tour/constants/styleGuide'
import idx from 'idx'
import textIcon from 'public/assets/svg/hotspots/fill_circle.svg'
import photoIcon from 'public/assets/svg/hotspots/photo_hotspot.svg'
import videoIcon from 'public/assets/svg/hotspots/video_hotspot.svg'
import panoIcon from 'public/assets/svg/hotspots/360_hotspot.svg'
import pmIcon from 'public/assets/svg/positional_media/play_i.svg'
import Instagram from 'public/assets/svg/instagram.svg'
import InstagramThumbnail from 'aria-viewer/tour/components/InstagramThumbnail'

const styleSheet = theme => ({
    container: {
        height: '100%',
    },

    bodyContainer: {
        height: '100%',
        overflowY: 'scroll',
        overflowScrolling: 'touch',
        padding: 16,
    },

    title: {
        fontSize: 24,
        fontWeight: 500,
        color: theme.palette.common.white,
    },

    titleContainer: {
        paddingTop: 10,
    },

    header: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        backgroundColor: 'rgb(30, 39, 59)',
        top: 0,
        left: 0,
        padding: 16,
    },

    headerRow: {
        width: '100%',
        display: 'flex',
    },

    leftButton: {
        flex: 3,
    },

    rightButtons: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        paddingRight: 16,
    },

    langugageButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },

    closeButton: {
        margin: '24px',
        position: 'absolute',
        top: '2px',
        left: '2px',
        cursor: 'pointer',
        width: '16px',
        height: '16px',
    },

    langugageButton: {
        width: 75,
        height: 34,
        '& > span': {
            display: 'flex',
        },
    },

    shareButton: {
        '& svg path': {
            fill: theme.palette.common.white,
        },

        '& div': {
            width: 18,
            height: 21,
        },
    },

    audioButton: {
        '& svg path': {
            fill: theme.palette.common.black,
        },
    },

    arrowButton: {
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 8,
        '& #Shape': {
            fill: theme.palette.common.white,
        },
    },

    readMoreContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },

    readMore: {
        marginTop: 6,
    },

    descriptionContainer: {
        padding: 16,
        backgroundColor: '#121927',
        borderRadius: 8,
        height: 272,
        boxShadow: '0 8px 10px 0 rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        marginBottom: 16,
        boxSizing: 'border-box',
    },

    description: {
        fontSize: 16,
        color: theme.palette.common.white,
        textAlign: 'left',
    },

    supplementalMediaContainer: {
        marginBottom: 122,
    },

    modal: {
        backgroundColor: '#121927',
    },

    thumbnailContainer: {
        position: 'relative',
    },

    thumbnailOverlay: {
        position: 'absolute',
        display: 'flex',
        flexFlow: 'row-reverse',
        top: '4px',
        right: '40px',
    },

    thumbNail: {
        height: 272,
        borderRadius: 8,
        boxShadow: '0 8px 10px 0 rgba(0, 0, 0, 0.3)',
        marginBottom: 16,
    },

    instagramThumbnail: {
        marginBottom: 15,
        borderRadius: 4,
    },

    icon: {
        color: '#fff',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
    },
})

const inlineStyle = {
    shareButton: {
        display: 'flex',
        width: 36,
        height: 36,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 0,
    },

    audioButton: {
        width: 36,
        height: 36,
        marginRight: 10,
        background: '#CED0D4',
    },

    arrowButton: {
        width: 8,
        height: 5,
    },

    readMoreButton: {
        fontSize: 16,
        color: '#fff',
    },
}

const iconNames = {
    text: textIcon,
    photo: photoIcon,
    video: videoIcon,
    panorama: panoIcon,
    positionalMedia: pmIcon,
    instagram: Instagram,
}

const style = injectSheet(styleSheet)

const getOneTrustSafeImgSrc = imgSrc => {
    return typeof imgSrc === 'string' ? imgSrc.replace('img.youtube.com', 'i.ytimg.com') : imgSrc
}

const getMediaIcon = (type, network) => {
    let iconType = network === NETWORK_TYPES.instagram ? 'instagram' : type

    return iconNames[iconType]
}

const StopDescription = ({
    classes,
    title,
    description,
    language,
    supplementalMedia,
    currentModal,
    currentMedia,
    onClose,
    onMediaClick,
    onModalClose,
    onReadMoreClick,
    containerSize,
    isVrModuleEnabled,
}) => (
    <div className={classes.container}>
        <div className={classes.header}>
            <div className={classes.headerRow}>
                <span className={classes.leftButton}>
                    <CloseButton className={classes.closeButton} onClick={onClose} />
                </span>

                <span className={classes.rightButtons}>
                    {isVrModuleEnabled && (
                        <VrButton
                            className={classes.vrButton}
                            buttonLocation={VR_BUTTON_LOCATION.mobile.tourButtonPanel}
                            theme={styleguide.buttons.infoMenuButton.vr}
                        />
                    )}
                    <AudioButton
                        className={classes.audioButton}
                        puresvg={true}
                        style={inlineStyle.audioButton}
                        onIcon="speaker_on"
                        offIcon="speaker_off"
                        enableCountdown={false}
                    />

                    <ShareButton style={inlineStyle.shareButton} className={classes.shareButton} />
                </span>
            </div>

            <div className={classNames(classes.headerRow, classes.titleContainer)}>
                <span className={classes.title}>{title}</span>
            </div>
        </div>

        <div className={classes.bodyContainer}>
            {description.length > 0 && (
                <div className={classes.descriptionContainer}>
                    <Clamp clamp={9}>
                        <div className={classes.description} dangerouslySetInnerHTML={{ __html: description }} />
                    </Clamp>
                    {description.length > 350 && ( //ballpark overflow based on current clamp. So read more will realtively show when needed.
                        <div className={classes.readMoreContainer}>
                            <Button
                                className={classes.readMore}
                                labelStyle={inlineStyle.readMoreButton}
                                onClick={onReadMoreClick}
                            >
                                Read more
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <div className={classes.supplementalMediaContainer}>
                {supplementalMedia.map(media => {
                    return (
                        <div className={classes.thumbnailContainer} key={media.id}>
                            <div className={classes.thumbnailOverlay}>
                                <img className={classes.icon} src={`${getMediaIcon(media.mediaType, media.network)}`} />
                            </div>
                            {(network => {
                                switch (network) {
                                    case NETWORK_TYPES.instagram:
                                        return (
                                            <InstagramThumbnail
                                                className={classes.instagramThumbnail}
                                                onClick={() => onMediaClick(media)}
                                                embedId={
                                                    media.mediaType === MEDIA_TYPES.photo ? media.url : media.videoid
                                                }
                                                height={272}
                                                top={-54}
                                                left={0}
                                                iframeHeight={'120%'}
                                            />
                                        )
                                    default:
                                        return (
                                            <MediaImage
                                                className={classes.thumbNail}
                                                onClick={() => onMediaClick(media)}
                                                key={media.id}
                                                url={getOneTrustSafeImgSrc(media.urls.medium)}
                                            />
                                        )
                                }
                            })(media.network)}
                        </div>
                    )
                })}
            </div>
        </div>

        <Modal className={classes.modal} open={currentModal !== null}>
            {
                {
                    [StopDescriptionModals.MORE_DESCRIPTION]: (
                        <MoreDescription
                            title={title}
                            description={description}
                            language={language}
                            onClose={onModalClose}
                            isVrModuleEnabled={isVrModuleEnabled}
                        />
                    ),

                    [StopDescriptionModals.MEDIA_VIEW]: (
                        <MediaFullScreen
                            media={currentMedia}
                            mediaUrl={idx(currentMedia, _ => _.urls.medium)}
                            title={idx(currentMedia, _ => _.title)}
                            description={idx(currentMedia, _ => _.description)}
                            onClose={onModalClose}
                            containerSize={containerSize}
                        />
                    ),

                    default: null,
                }[currentModal]
            }
        </Modal>
    </div>
)

export default compose(style, pure)(StopDescription)
